import { getGlobalThat } from './global-that';

type Ctor = abstract new (...args: any) => any;

function registerStore(): void {
  const globalAny: any = getGlobalThat();
  if (!globalAny['_diStore']) {
    globalAny['_diStore'] = new Map();
    globalAny['_diStoreReplace'] = new Map();
  }
}

function getStore<T extends Ctor>(): Map<string, InstanceType<T> | undefined> {
  const globalAny: any = getGlobalThat();
  return globalAny['_diStore'];
}

function getReplaceStore<R extends Ctor>(): Map<string, () => InstanceType<R>> {
  const globalAny: any = getGlobalThat();
  return globalAny['_diStoreReplace'];
}

// Decision: see ADR-0005 — keying by class reference caused a duplicated singleton in production on Safari.
function getClassKey<T extends Ctor>(klazz: T): string {
  /**
   * Generate a unique key for a class based on its name and constructor content.
   *
   * We cannot use the class reference directly as a Map key because of Safari's
   * module loading behavior. In Safari, when the same class is imported both
   * statically and dynamically (via import()), they are treated as different
   * object references, even though they represent the same class.
   *
   * This causes singleton instances to be created multiple times:
   * 1. First instance: created during static module loading
   * 2. Second instance: created when dynamic imports execute
   *
   * Safari creates separate evaluation contexts for dynamic imports, causing
   * class references to differ between static and dynamic import contexts.
   * Other browsers (Chrome, Firefox) properly share class references.
   *
   * By using className + constructor hash as the key, we ensure the same
   * singleton instance regardless of how the class was imported.
   */
  const constructorStr: string = klazz.toString();
  const hash: string = btoa(constructorStr);
  return `${klazz.name}_${hash}`;
}

export function provideSingleton<T extends Ctor>(klazz: T, singletonFactory: () => InstanceType<T>): InstanceType<T> {
  registerStore();
  const diStore: Map<string, InstanceType<T> | undefined> = getStore<T>();
  const replaceStore: Map<string, () => InstanceType<T>> = getReplaceStore();

  const classKey: string = getClassKey(klazz);

  const fixedSingletonFactory: () => InstanceType<T> = replaceStore.get(classKey) ?? singletonFactory;

  if (!diStore.has(classKey)) {
    diStore.set(classKey, fixedSingletonFactory());
  }
  return diStore.get(classKey)!;
}

export function replaceDiImpl<T extends Ctor, R extends Ctor>(klazz: T, singletonFactory: () => InstanceType<R>): void {
  registerStore();
  const diStore: Map<string, InstanceType<T> | undefined> = getStore<T>();
  const replaceStore: Map<string, () => InstanceType<R>> = getReplaceStore();
  const classKey: string = getClassKey(klazz);

  if (diStore.has(classKey)) {
    console.error('already initialized singleton!! wrong!!!', klazz);
    return;
  }
  if (replaceStore.has(classKey)) {
    console.warn('You already set replaceDi for class', klazz);
  }
  replaceStore.set(classKey, singletonFactory);
}

export function destroySingleton<T extends Ctor>(klazz: T): void {
  registerStore();
  const diStore: Map<string, InstanceType<T> | undefined> = getStore<T>();
  const replaceStore: Map<string, () => InstanceType<T>> = getReplaceStore();

  diStore.delete(getClassKey(klazz));
  replaceStore.delete(getClassKey(klazz));
}
